import React, { useState, useEffect, useMemo } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const NO_DURATION = 1000;
const usd = n => (n === null || n === undefined) ? '—' : '$' + Number(n).toFixed(2);
const durLabel = m => m === NO_DURATION || m === null ? 'no duration' : m === 0 ? 'Lifetime' : m < 0 ? `${-m} days` : `${m} mo`;
// Words that say nothing about WHICH product it is.
const STOP = new Set(['subscription', 'account', 'accounts', 'key', 'keys', 'pro', 'premium', 'plan', 'month', 'months', 'year', 'years', 'aylık', 'aylik', 'ay', 'yıllık', 'yillik', 'günlük', 'gunluk', 'the', 'and', 'for', 'with', '-', '|']);
const words = s => String(s || '').toLowerCase().split(/[^a-z0-9çğıöşü]+/i).filter(w => w.length > 1 && !STOP.has(w) && !/^\d+$/.test(w));

/**
 * "Supplier stock" for ONE of the admin's own Marketplace products: per
 * duration, which supplier product restocks it. Own codes always sell first;
 * after that each sale (website and bot) is bought from the chosen supplier.
 */
function SupplierSourceEditor({ product, ownStock = {}, onClose, onSaved }) {
    const [supplierRows, setSupplierRows] = useState(null);
    const [rate, setRate] = useState(1);
    const [current, setCurrent] = useState({});   // month -> supplierBotId (saved)
    const [choice, setChoice] = useState({});     // month -> supplierBotId|'' (edited)
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const months = (Array.isArray(product.enabledMonths) && product.enabledMonths.length ? product.enabledMonths : [1]).map(Number);
    const priceOf = m => Number(product.priceByMonth?.[m] ?? product.priceByMonth?.[String(m)] ?? 0);

    useEffect(() => {
        const token = localStorage.getItem('adminToken');
        const get = p => fetch(`${API_URL}${p}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => ({}));
        Promise.all([get('/admin/marketplace/supplier-products'), get('/admin/marketplace/supplier-links')]).then(([sp, ln]) => {
            setSupplierRows((sp.products || []).filter(r => !r.deleted && !r.gone && r.instant && !r.requiresEmail));
            setRate(sp.usdPerGc || 1);
            const cur = {};
            for (const l of ln.links || []) if (l.productId === product.id) cur[l.durationMonths] = l.supplierBotId;
            setCurrent(cur);
            setChoice(cur);
        });
    }, [product.id]);

    // Candidates per duration, best first: same duration + shared name words.
    const ranked = useMemo(() => {
        const out = {};
        if (!supplierRows) return out;
        const mine = words(product.name);
        for (const m of months) {
            out[m] = supplierRows.map(r => {
                const shared = words(r.name).filter(w => mine.includes(w)).length;
                const sameDur = Number(r.months) === m;
                return { r, score: shared * 10 + (sameDur ? 5 : 0), shared, sameDur };
            }).sort((a, b) => b.score - a.score || a.r.costUsd - b.r.costUsd);
        }
        return out;
    }, [supplierRows, product.name]); // eslint-disable-line react-hooks/exhaustive-deps

    const autoMatch = () => {
        const next = { ...choice };
        let n = 0;
        for (const m of months) {
            const best = (ranked[m] || []).find(c => c.shared > 0 && c.sameDur);
            if (best && !next[m]) { next[m] = best.r.botId; n++; }
        }
        setChoice(next);
        setError(n ? null : 'No supplier product with the same name and duration was found — pick them by hand.');
    };

    const save = async () => {
        setBusy(true); setError(null);
        const token = localStorage.getItem('adminToken');
        const changed = months.filter(m => (choice[m] || null) !== (current[m] || null));
        for (const m of changed) {
            const res = await fetch(`${API_URL}/admin/marketplace/supplier-links`, {
                method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ productId: product.id, durationMonths: m, supplierBotId: choice[m] || null })
            }).catch(() => null);
            const body = res ? await res.json().catch(() => ({})) : {};
            if (!res || !res.ok) { setError(`${durLabel(m)}: ${body.error || 'could not save'}`); setBusy(false); return; }
        }
        setBusy(false);
        onSaved && onSaved(changed.length);
        onClose();
    };

    const manual = product.manualFulfill || product.requiresEmail;
    const byId = id => (supplierRows || []).find(r => r.botId === Number(id));

    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div onClick={e => e.stopPropagation()} className="card" style={{ margin: 0, padding: 20, width: 'min(760px, 100%)', maxHeight: '88vh', overflow: 'auto', borderRadius: 12 }}>
                <h3 style={{ marginTop: 0, marginBottom: 4 }}>🔗 Supplier stock — {product.name}</h3>
                <p style={{ fontSize: 13, color: '#64748b', marginTop: 0 }}>
                    Choose a supplier product for each duration. Your own codes still sell first; when they run out, each sale
                    (website and Telegram bot) is bought from that supplier automatically and the stock shown includes theirs.
                    A duration whose price is at or below the supplier cost never uses the supplier.
                </p>
                {manual ? (
                    <div style={{ color: '#b45309' }}>This is a hand-delivered / email-activation product, so it can't be restocked automatically.</div>
                ) : !supplierRows ? (
                    <div style={{ color: '#64748b' }}>Loading supplier products…</div>
                ) : !supplierRows.length ? (
                    <div style={{ color: '#64748b' }}>No supplier products yet — link a supplier in Telegram Shop → Suppliers.</div>
                ) : (
                    <>
                        <button type="button" className="btn btn-secondary" style={{ marginBottom: 12 }} onClick={autoMatch}>✨ Auto-match by name &amp; duration</button>
                        <div style={{ display: 'grid', gap: 10 }}>
                            {months.map(m => {
                                const sel = byId(choice[m]);
                                const price = priceOf(m);
                                const loss = sel && !(price * rate > Number(sel.costUsd));
                                return (
                                    <div key={m} style={{ display: 'grid', gridTemplateColumns: 'minmax(110px, 140px) 1fr', gap: 10, alignItems: 'center' }}>
                                        <div>
                                            <b>{durLabel(m)}</b>
                                            <div style={{ fontSize: 12, color: '#64748b' }}>{price} GC · {ownStock[m] ?? 0} own codes</div>
                                        </div>
                                        <div>
                                            <select className="form-input" value={choice[m] || ''} onChange={e => setChoice(c => ({ ...c, [m]: e.target.value ? Number(e.target.value) : null }))}
                                                style={{ borderColor: loss ? '#dc2626' : undefined }}>
                                                <option value="">— none (own codes only) —</option>
                                                {(ranked[m] || []).map(({ r, shared, sameDur }) => (
                                                    <option key={r.botId} value={r.botId}>
                                                        {shared && sameDur ? '★ ' : ''}{r.name} · {r.supplierName} · cost {usd(r.costUsd)} · {r.inStock ? (r.stock > 0 ? `${r.stock} in stock` : 'in stock') : 'out of stock'}
                                                    </option>
                                                ))}
                                            </select>
                                            {sel && (
                                                <div style={{ fontSize: 12, marginTop: 3, color: loss ? '#dc2626' : '#15803d' }}>
                                                    {loss
                                                        ? `Your price ${price} GC is not above the cost ${usd(sel.costUsd)} — the supplier will NOT be used. Raise the price.`
                                                        : `Profit per supplier sale ≈ ${usd(price * rate - sel.costUsd)}${sel.inStock ? '' : ' · supplier is out of stock right now'}`}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
                {error && <div style={{ color: '#dc2626', fontSize: 13, marginTop: 10 }}>{error}</div>}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                    <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
                    {!manual && <button type="button" className="btn btn-primary" disabled={busy || !supplierRows} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>}
                </div>
            </div>
        </div>
    );
}

export default SupplierSourceEditor;
